import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  return (
    <div className="max-w-4xl">
      <Tabs defaultValue="printer">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="printer" data-testid="tab-printer">Printer</TabsTrigger>
          <TabsTrigger value="receipt" data-testid="tab-receipt">Receipt Template</TabsTrigger>
          <TabsTrigger value="label" data-testid="tab-label">Label Template</TabsTrigger>
          <TabsTrigger value="store" data-testid="tab-store">Store</TabsTrigger>
          <TabsTrigger value="scanner" data-testid="tab-scanner">Scanner</TabsTrigger>
          {isOwner && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
        </TabsList>
        <TabsContent value="printer"><PrinterSettings /></TabsContent>
        <TabsContent value="receipt"><ReceiptTemplate /></TabsContent>
        <TabsContent value="label"><LabelTemplate /></TabsContent>
        <TabsContent value="store"><StoreSettings /></TabsContent>
        <TabsContent value="scanner"><ScannerSettings /></TabsContent>
        {isOwner && <TabsContent value="users"><Users /></TabsContent>}
      </Tabs>
    </div>
  );
}

function PrinterSettings() {
  const [s, setS] = useState({ api_key: "", receipt_printer_id: null, label_printer_id: null, auto_print: true });
  const [printers, setPrinters] = useState([]);
  const [configured, setConfigured] = useState(false);
  useEffect(() => { api.get("/print/settings").then((r) => { setS({ ...r.data, api_key: "" }); setConfigured(!!r.data.api_key_set); }).catch(() => {}); }, []);
  const listPrinters = () => api.get("/print/printers").then((r) => {
    setPrinters(r.data.printers || []);
    if (r.data.error) { toast.error(r.data.error); }
    else if ((r.data.printers || []).length === 0) { toast.info("No printers found for this PrintNode account."); }
    else { toast.success(`Loaded ${r.data.printers.length} printer(s)`); }
  }).catch((e) => toast.error(formatApiError(e)));
  const save = () => api.put("/print/settings", s).then(() => { toast.success("Printer settings saved"); setConfigured(true); setS((p) => ({ ...p, api_key: "" })); }).catch((e) => toast.error(formatApiError(e)));
  const test = () => api.post("/print/test").then((r) => {
    if (r.data.ok) { toast.success("Receipt test sent"); }
    else { toast.error(r.data.error || "Test print failed"); }
  }).catch((e) => toast.error(formatApiError(e)));
  const testLabel = () => api.post("/print/test-label").then((r) => {
    if (r.data.ok) { toast.success("Label test sent to the Zebra"); }
    else { toast.error(r.data.error || "Label test failed"); }
  }).catch((e) => toast.error(formatApiError(e)));

  return (
    <Card className="p-5 space-y-4 max-w-xl">
      <div className="flex items-center gap-2 text-sm text-slate-600"><Printer className="w-4 h-4" />PrintNode cloud printing {configured && <Badge className="bg-emerald-100 text-emerald-700">Key set</Badge>}</div>
      <div><Label className="text-xs">PrintNode API key {configured && "(leave blank to keep current)"}</Label><Input value={s.api_key} onChange={(e) => setS({ ...s, api_key: e.target.value })} placeholder="pn_..." data-testid="printnode-key-input" /></div>
      <Button variant="outline" size="sm" onClick={listPrinters} data-testid="list-printers-btn">Load printers</Button>
      {printers.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Receipt printer</Label>
            <Select value={String(s.receipt_printer_id || "")} onValueChange={(v) => setS({ ...s, receipt_printer_id: +v })}><SelectTrigger data-testid="receipt-printer-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{printers.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name} ({p.state})</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Label printer (Zebra)</Label>
            <Select value={String(s.label_printer_id || "")} onValueChange={(v) => setS({ ...s, label_printer_id: +v })}><SelectTrigger data-testid="label-printer-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{printers.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name} ({p.state})</SelectItem>)}</SelectContent></Select></div>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm"><Switch checked={s.auto_print} onCheckedChange={(v) => setS({ ...s, auto_print: v })} data-testid="auto-print-switch" /> Auto-print receipt on sale</label>
      <div className="flex flex-wrap gap-2"><Button onClick={save} data-testid="save-printer-btn">Save</Button><Button variant="outline" onClick={test} data-testid="test-print-btn">Test receipt</Button><Button variant="outline" onClick={testLabel} data-testid="test-label-btn">Test label</Button></div>
      <p className="text-xs text-slate-400">Install the PrintNode client on the counter PC connected to your printers, create an API key, and paste it here. Queued receipts auto-retry every 30 seconds if the printer is offline.</p>
    </Card>
  );
}

function ReceiptTemplate() {
  const [t, setT] = useState({ header: "", footer: "", message: "", show_payment: true, show_customer: true, show_budtender: true });
  useEffect(() => { api.get("/settings/receipt_template").then((r) => setT({ ...t, ...r.data })).catch(() => {}); }, []);
  const save = () => api.put("/settings/receipt_template", t).then(() => toast.success("Saved")).catch((e) => toast.error(formatApiError(e)));
  return (
    <Card className="p-5 space-y-3 max-w-xl">
      <div><Label className="text-xs">Header</Label><Input value={t.header} onChange={(e) => setT({ ...t, header: e.target.value })} data-testid="receipt-header-input" /></div>
      <div><Label className="text-xs">Custom message</Label><Textarea value={t.message} onChange={(e) => setT({ ...t, message: e.target.value })} rows={2} /></div>
      <div><Label className="text-xs">Footer</Label><Input value={t.footer} onChange={(e) => setT({ ...t, footer: e.target.value })} data-testid="receipt-footer-input" /></div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm"><Switch checked={t.show_payment} onCheckedChange={(v) => setT({ ...t, show_payment: v })} /> Show payment method</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={t.show_customer} onCheckedChange={(v) => setT({ ...t, show_customer: v })} /> Show customer name</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={t.show_budtender} onCheckedChange={(v) => setT({ ...t, show_budtender: v })} /> Show budtender</label>
      </div>
      <Button onClick={save} data-testid="save-receipt-btn">Save</Button>
    </Card>
  );
}

function LabelTemplate() {
  return (
    <Card className="p-5 space-y-4 max-w-xl" data-testid="label-template-card">
      <div>
        <div className="text-sm font-medium text-slate-700">Flower label layout</div>
        <p className="text-xs text-slate-400 mt-1">Labels print automatically only for <span className="font-medium text-slate-600">Flower</span> products on the Zebra printer. Each label shows four fields, pulled from the strain:</p>
      </div>
      {/* Visual preview of a real flower label */}
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 max-w-[260px]" data-testid="label-preview">
        <div className="bg-white rounded-md border border-slate-200 px-4 py-3 shadow-sm">
          <div className="text-lg font-bold text-slate-900 leading-tight" data-testid="label-preview-name">Blue Dream</div>
          <div className="text-sm font-semibold text-emerald-700 tracking-wide" data-testid="label-preview-type">HYBRID</div>
          <div className="text-sm text-slate-700 mt-1" data-testid="label-preview-thca">THCA 24.6%</div>
          <div className="text-sm text-slate-700" data-testid="label-preview-d9">Delta-9 0.3%</div>
        </div>
      </div>
      <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
        <li>Strain name</li>
        <li>Strain type (Indica / Sativa / Hybrid)</li>
        <li>THCA percentage</li>
        <li>Delta-9 percentage</li>
      </ul>
      <p className="text-xs text-slate-400">These values come from each flower product's details — edit them under <span className="font-medium text-slate-600">Products</span>. Use the <span className="font-medium text-slate-600">Test label</span> button on the Printer tab to send a sample to your Zebra.</p>
    </Card>
  );
}

function StoreSettings() {
  const [t, setT] = useState({ name: "", address: "", phone: "" });
  useEffect(() => { api.get("/settings/store").then((r) => setT({ ...t, ...r.data })).catch(() => {}); }, []);
  const save = () => api.put("/settings/store", t).then(() => toast.success("Saved")).catch((e) => toast.error(formatApiError(e)));
  return (
    <Card className="p-5 space-y-3 max-w-xl">
      {[["name", "Store name"], ["address", "Address"], ["phone", "Phone"]].map(([k, l]) => (
        <div key={k}><Label className="text-xs">{l}</Label><Input value={t[k] || ""} onChange={(e) => setT({ ...t, [k]: e.target.value })} data-testid={`store-${k}-input`} /></div>
      ))}
      <Button onClick={save} data-testid="save-store-btn">Save</Button>
    </Card>
  );
}

function ScannerSettings() {
  const [t, setT] = useState({ enabled: true, prefix: "", suffix: "" });
  useEffect(() => { api.get("/settings/scanner").then((r) => setT({ ...t, ...r.data })).catch(() => {}); }, []);
  const save = () => api.put("/settings/scanner", t).then(() => toast.success("Saved")).catch((e) => toast.error(formatApiError(e)));
  return (
    <Card className="p-5 space-y-3 max-w-xl">
      <label className="flex items-center gap-2 text-sm"><Switch checked={t.enabled} onCheckedChange={(v) => setT({ ...t, enabled: v })} data-testid="scanner-enabled-switch" /> Barcode scanner enabled</label>
      <div><Label className="text-xs">Prefix</Label><Input value={t.prefix || ""} onChange={(e) => setT({ ...t, prefix: e.target.value })} /></div>
      <div><Label className="text-xs">Suffix</Label><Input value={t.suffix || ""} onChange={(e) => setT({ ...t, suffix: e.target.value })} /></div>
      <Button onClick={save} data-testid="save-scanner-btn">Save</Button>
    </Card>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setEditing({ email: "", name: "", role: "budtender", password: "", pin: "", active: true })} className="bg-indigo-600" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1.5" />New user</Button></div>
      <Card className="divide-y">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`user-row-${u.id}`}>
            <div className="flex-1"><span className="font-medium">{u.name}</span><div className="text-xs text-slate-400">{u.email}</div></div>
            <Badge variant="secondary" className="capitalize">{u.role}</Badge>
            {u.active === false && <Badge className="bg-red-100 text-red-700">Inactive</Badge>}
            <Button variant="ghost" size="sm" onClick={() => setEditing({ ...u, password: "", pin: "" })} data-testid={`edit-user-${u.id}`}>Edit</Button>
          </div>
        ))}
      </Card>
      {editing && <UserDialog u={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function UserDialog({ u, onClose, onSaved }) {
  const [f, setF] = useState(u);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const payload = { email: f.email, name: f.name, role: f.role, active: f.active };
      if (f.password) payload.password = f.password;
      if (f.pin) payload.pin = f.pin;
      if (u.id) await api.patch(`/users/${u.id}`, payload); else await api.post("/users", payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{u.id ? "Edit" : "New"} user</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" /></div>
          <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} disabled={!!u.id} data-testid="user-email-input" /></div>
          <div><Label className="text-xs">Role</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="owner">Owner</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="budtender">Budtender</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Password {u.id && "(blank = unchanged)"}</Label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" /></div>
          <div><Label className="text-xs">PIN (optional)</Label><Input value={f.pin} onChange={(e) => set("pin", e.target.value)} data-testid="user-pin-input" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} /> Active</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-user-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
